import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { environment } from '@env/environment';
import { PaymentPeriod, SitePayment } from '@shared/models';
import { deserialize, serialize } from '@shared/models/base-helper';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { selectIsMobileView } from '@store/app-config/app-config.selectors';
import { Store } from '@ngrx/store';
import * as fromApp from '@store';
import { DialogYesNoComponent } from '@shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { AppConfigService } from '@core/api/app-config.service';
import { MatDialog } from '@angular/material/dialog';
import { BulkProgress, ServerSentEventService } from '@core/api/server-sent-event.service';

export interface StripeLoginLink {
  object: 'login_link';
  created: number;
  url: string;
  id: string;
}

interface PaymentData {
  site_id: number;
  period: PaymentPeriod;
}

export interface PaymentSitesSerializedResponse {
  payments: Array<any>;
  total_paid_in_cents: number;
  has_error: boolean;
}

export interface PaymentSitesResponse {
  payments: Array<SitePayment>;
  totalPaidInCents: number;
  hasError: boolean;
}

export interface SendStripeEmailRequest {
  site_ids: Array<number>;
}


@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  apiUrl = environment.apiUrl + '/payments/';

  constructor(
    readonly http: HttpClient, readonly spinnerService: SpinnerService,
    readonly appConfigService: AppConfigService,
    readonly serverSentEventService: ServerSentEventService,
    readonly dialog: MatDialog,
    readonly store: Store<fromApp.AppState>
  ) {}

  getMyLoginUrl() {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Notice',
          line1: `You are leaving LandYield and going to our third-party payment provider, Stripe.`,
          buttonTextYes: 'Continue',
          ...this.appConfigService.getPopupDetails('stripe-open')
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe(result => {
        this.spinnerService.show('app-spinner', 'Redirecting to Stripe');
        this.http
          .get<StripeLoginLink>(`${this.apiUrl}my_login_url`,)
          .subscribe({
            next: (response: any) => {
              if (response?.url) {
                this.store
                  .select(selectIsMobileView)
                  .pipe(take(1))
                  .subscribe(mobileViewMode => {
                    if (mobileViewMode) {
                      window.location.href = response.url
                    } else {
                      window.open(response.url, '_blank', 'noopener,noreferrer');
                    }
                  });
              }
              this.spinnerService.hide('app-spinner');
            },
            error: error => {
              this.spinnerService.hide('app-spinner');
              console.log('Error getting Stripe URL', error);
              return throwError(() => new Error(error));
            }
          });
      })
  }

  sendStripeEmails(siteIds: Array<number>): Observable<Array<string>> {
    const body: SendStripeEmailRequest = {
      site_ids: siteIds,
    };
    return this.http
      .post<SendStripeEmailRequest>(`${this.apiUrl}stripe-emails`, body)
      .pipe(map((value: any) => value.emails));
  }

  paySites(data: Array<PaymentData>): Observable<BulkProgress> {
    const sites = data.map(d => {
      return {
        site_id: d.site_id,
        period: serialize(d.period),
      };
    });
    const payload = { sites };
    return this.serverSentEventService.bulkUpdateWithProgress(`${this.apiUrl}pay_sites`, payload);
  }

  getHistory(siteId: number): Observable<SitePayment[]> {
    return this.http
      .get<any[]>(`${this.apiUrl}history?site_id=${siteId}`)
      .pipe(map(payments => payments.map((serializedPayment: any) => deserialize(serializedPayment, SitePayment))));
  }

  recordPayment({ data }): Observable<SitePayment> {
    const payment = { date: data.date, amount: data.amount, note: data.note };
    const url = `${this.apiUrl}record?site_id=${data.siteId}`;
    return this.http
      .post<any>(url, payment)
      .pipe(map((serializedPayment: any) => deserialize(serializedPayment, SitePayment)));
  }

  expressPayment({ data }): Observable<SitePayment> {
    const payment = { date: data.date, amount: data.amount, note: data.note };
    const url = `${this.apiUrl}express?site_id=${data.siteId}`;
    return this.http
      .post<any>(url, payment)
      .pipe(map((serializedPayment: any) => deserialize(serializedPayment, SitePayment)));
  }

  getStripeBalance(): Observable<number> {
    return this.http.get<any>(`${this.apiUrl}stripe-balance`).pipe(
      map((response: number) => response)
    );
  }
}
